
import React, { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { IoChatbubbleEllipsesSharp } from "react-icons/io5";
import { Modal, ModalBody, SIZE } from "baseui/modal";
import axios from 'axios';
import './tools.css'; 

export default function Chatbot({ user }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hey, I'm Manny. What can I help you with?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const audioRef = useRef(null);

  const switchButton = (msg) => {
    if(isSpeaking) return (
      <Button 
        className="read-aloud-button"
        id="stop" 
        onClick={() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
            setIsSpeaking(false);
          }
        }}
      >
        🔇 Stop Reading
      </Button>
    );
    return (
      <Button
        onClick={() => speakText(msg.content)}
        id="start"
        className="read-aloud-button"
        disabled={isSpeaking}
      >
        🔊 Read Aloud
      </Button>
    );
  }

  const speakText = async (text) => {
    const voice = "ash";
    const apiKey = process.env.REACT_APP_MANNY_OPENAI_API_KEY;

    if (!text || !apiKey) return;

    try {
      // Stop any current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        setIsSpeaking(false);
      }

      setIsSpeaking(true);

      const response = await fetch("https://api.openai.com/v1/audio/speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "tts-1",
          input: text,
          voice: voice,
        }),
      });

      if (!response.ok) {
        throw new Error("TTS API request failed.");
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audioRef.current = audio;
      audio.onplay = () => {
        setIsSpeaking(true);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        audioRef.current = null;
      };

      await audio.play(); // ⏱️ trigger playback (can take a second)
    } catch (error) {
      console.error("OpenAI TTS error:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsSpeaking(false);
      }
    };
  }, []);

  const sendMessage = async (messageText) => {
    const trimmed = messageText || input.trim();
    if (!trimmed) return;

    const updatedMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/manny`,
        {
          userId: user?.id,
          message: trimmed
        }
      );

      const reply = res?.data.reply;

      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: reply }
      ]);

      speakText(reply);
    } catch (err) {
      console.error(err);
      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: 'Sorry, something went wrong.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div> 
      <Button
        variant="primary"
        className="chatbot-button" 
        style={{ 
          borderRadius: '80px', 
          fontSize: '2em', 
          padding: '0.5em', 
          position: 'fixed', 
          right: '1em',
          top: '90vh', 
        }}
        onClick={() => setIsModalOpen(true)}
      >
        <IoChatbubbleEllipsesSharp />
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        closeable
        animated
        size={SIZE.default}
        className="chatbot-container"
        overrides={{
          Dialog: {
            style: {
              width: '90%',
              maxWidth: '600px',
              height: '80%',
              maxHeight: '80%',
              marginTop: 'auto',
              marginBottom: 'auto',
              marginLeft: 'auto',
              marginRight: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }
          },
          Backdrop: {
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <ModalBody>
          <div className="chatbot-container">
            <div className="chatbot-header">
              <h2>Chat with Manny</h2>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role}`} id="msg-txt">
                  {msg.content}
                  {msg.role === 'assistant' && msg.content !== messages[0].content && switchButton(msg)}
                </div>
              ))}
            </div>
              {isSpeaking && <span style={{ color: "green", fontWeight: "bold" }}>🔊 Manny is talking...</span>}
            <div className="chatbot-input">
              <input
                className="chatbot-input-field"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Send a message to Manny..."
                style={{border: 'solid 1px lightgray', borderRadius: '1em 0% 0% 1em', height: '40px', width: '100%'}}
              />
              <Button
                variant="primary"
                onClick={() => sendMessage(input)}
                disabled={loading}
                style={{border: 'none', borderRadius: '0 1em 1em 0%', backgroundColor: "green", color: 'white'}}
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </div>
              <div className="chatbot-close-button">
                <Button
                  className="close-chatbot-button" 
                  variant="secondary" 
                  onClick={() => setIsModalOpen(false)}
                >
                  Close
                </Button>
              </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}
